import { formatPhoneForApi } from "./contactValidation";
import { RIDE_ORDER_REQUEST_CLIENT } from "./rideOrderClientMeta";
import { getVehicleById } from "./vehicleData";
import { joinScheduleDateTime } from "./scheduleDateTime";
import { isScheduleRide, OrderFormState, RideType } from "./types";

export type RideOrderWaypointPayload = {
  lat: number;
  lng: number;
  address: string;
};

export type RideOrderEstimatePayload = {
  lat: number;
  lng: number;
  location: string;
  destination_lat: number;
  destination_lng: number;
  destination: string;
  waypoints?: RideOrderWaypointPayload[];
  car_type?: number;
  car_model?: string;
  hours?: number;
};

export type RideOrderRequestPayload = RideOrderEstimatePayload & {
  user_id: number;
  app_version: string;
  device_type: string;
  platform: string;
  device_version: string;
  customerName?: string;
  customerPhone?: string;
  scheduled?: 1;
  schedule_time?: string;
  num_of_passengers?: number;
  num_of_baggage?: number;
  additional_comment?: string;
  flight_number?: string;
  child_seat?: 0 | 1;
};

function mapWaypoints(form: OrderFormState): RideOrderWaypointPayload[] | undefined {
  const waypoints = form.waypoints
    .filter((waypoint) => waypoint.coords && waypoint.address.trim())
    .map((waypoint) => ({
      lat: waypoint.coords!.lat,
      lng: waypoint.coords!.lng,
      address: waypoint.address.trim(),
    }));

  return waypoints.length > 0 ? waypoints : undefined;
}

function mapCarFields(form: OrderFormState, rideType: RideType) {
  if (isScheduleRide(rideType)) {
    const vehicle = getVehicleById(form.vehicleId);
    return {
      car_model: vehicle.apiCarModel,
      hours: form.transferMode ? 0 : form.durationHours,
    };
  }

  return {
    car_type: form.carClass ?? 2,
  };
}

function baseRoutePayload(form: OrderFormState): RideOrderEstimatePayload | null {
  if (!form.pickupCoords || !form.dropoffCoords) return null;
  if (!form.pickup.trim() || !form.dropoff.trim()) return null;

  return {
    lat: form.pickupCoords.lat,
    lng: form.pickupCoords.lng,
    location: form.pickup.trim(),
    destination_lat: form.dropoffCoords.lat,
    destination_lng: form.dropoffCoords.lng,
    destination: form.dropoff.trim(),
    waypoints: mapWaypoints(form),
  };
}

export function canEstimateRidePrice(form: OrderFormState, rideType: RideType): boolean {
  if (!baseRoutePayload(form)) return false;

  if (isScheduleRide(rideType)) {
    return Boolean(form.vehicleId);
  }

  return form.carClass != null;
}

export function buildRideOrderEstimatePayload(
  form: OrderFormState,
  rideType: RideType
): RideOrderEstimatePayload | null {
  const route = baseRoutePayload(form);
  if (!route) return null;

  return {
    ...route,
    ...mapCarFields(form, rideType),
  };
}

export function buildPriceEstimateDependencyKey(
  form: OrderFormState,
  rideType: RideType
): string {
  return JSON.stringify({
    rideType,
    pickup: form.pickup,
    dropoff: form.dropoff,
    pickupCoords: form.pickupCoords,
    dropoffCoords: form.dropoffCoords,
    waypoints: form.waypoints.map((waypoint) => ({
      address: waypoint.address,
      coords: waypoint.coords,
    })),
    durationHours: form.durationHours,
    transferMode: form.transferMode,
    carClass: form.carClass,
    vehicleId: form.vehicleId,
  });
}

function mapContactFields(form: OrderFormState): Pick<RideOrderRequestPayload, "customerName" | "customerPhone"> {
  const customerName = form.name.trim();
  const customerPhone = formatPhoneForApi(form.phoneDialCode, form.phone);

  return {
    ...(customerName ? { customerName } : {}),
    ...(customerPhone ? { customerPhone } : {}),
  };
}

function formatScheduleTimeForApi(date: string, time: string): string | null {
  const scheduledAt = joinScheduleDateTime(date, time);
  if (!scheduledAt) return null;

  const pad = (value: number) => String(value).padStart(2, "0");
  const year = scheduledAt.getFullYear();
  const month = pad(scheduledAt.getMonth() + 1);
  const day = pad(scheduledAt.getDate());
  const hours = pad(scheduledAt.getHours());
  const minutes = pad(scheduledAt.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}:00+02:00`;
}

export function buildRideOrderRequestPayload(
  form: OrderFormState,
  rideType: RideType
): RideOrderRequestPayload | null {
  const estimatePayload = buildRideOrderEstimatePayload(form, rideType);
  if (!estimatePayload || !form.userId) return null;

  const payload: RideOrderRequestPayload = {
    ...estimatePayload,
    user_id: form.userId,
    ...RIDE_ORDER_REQUEST_CLIENT,
    ...mapContactFields(form),
    child_seat: form.childSeat ? 1 : 0,
  };

  if (isScheduleRide(rideType)) {
    const scheduleTime = formatScheduleTimeForApi(form.date, form.time);
    if (!scheduleTime) return null;

    payload.scheduled = 1;
    payload.schedule_time = scheduleTime;
    payload.num_of_passengers = form.passengers;
    payload.num_of_baggage = form.luggage;

    if (form.flightNumber.trim()) {
      payload.flight_number = form.flightNumber.trim();
    }

    if (form.comment.trim()) {
      payload.additional_comment = form.comment.trim();
    }
  }

  return payload;
}

export function formatRidePrice(amount: number): string {
  return new Intl.NumberFormat("sr-RS", {
    style: "currency",
    currency: "RSD",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function getRideOrderApiErrorMessage(error: unknown): string {
  const response = error as { data?: { message?: string; title?: string; errors?: Record<string, string[]> } };
  const data = response?.data;

  if (data?.message) return data.message;
  if (data?.errors) {
    const firstError = Object.values(data.errors).flat()[0];
    if (firstError) return firstError;
  }
  if (data?.title) return data.title;

  if (error instanceof Error && error.message) return error.message;

  return "Request failed";
}
